/**
 * Edit form for a field event.
 */
import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Edit, X } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { EventActivitySection } from './event-form-builder/EventActivitySection';
import { EventFormatSection } from './event-form-builder/EventFormatSection';
import { EventGeneralSection } from './event-form-builder/EventGeneralSection';
import { EventParticipantsSection } from './event-form-builder/EventParticipantsSection';
import { EventScheduleSection } from './event-form-builder/EventScheduleSection';
import { eventsService } from '@/services/events.service';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { eventValidationSchema } from '@/utils/validation-schemas-utils';
import { stubEstablishmentForSelect } from '@/utils/format-establishment-select-label-utils';
import {
  applyEventFieldSideEffects,
  buildUpdateEventDto,
  eventToFormData,
  extractEventParticipants,
  mapUnregisteredFromEvent,
} from '@/components/events/event-form-utils';
import type { UnregisteredStudentData, UpdateEventDto } from '@/types/event.types';
import type { UserWithProfile } from '@/types/user.types';

/** Props for {@link EditEventView}. */
interface EditEventViewProps {
  eventId: string;
  onClose: () => void;
  onSuccess: () => void;
  onRequestCreateTutor?: () => void;
}

/** Edit form for a field event. */
export const EditEventView: React.FC<EditEventViewProps> = ({
  eventId,
  onClose,
  onSuccess,
  onRequestCreateTutor,
}) => {
  const {
    data: event,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventsService.getEvent(eventId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'eventNotFound',
    onClose,
    entityId: eventId,
  });

  const [eventData, setEventData] = React.useState<Partial<UpdateEventDto>>({});
  const [selectedOrganizers, setSelectedOrganizers] = React.useState<UserWithProfile[]>([]);
  const [selectedStudents, setSelectedStudents] = React.useState<UserWithProfile[]>([]);
  const [unregisteredParticipants, setUnregisteredParticipants] = React.useState<
    UnregisteredStudentData[]
  >([]);

  /** Hydrate form + participant lists once GET event resolves. */
  React.useEffect(() => {
    if (!event) return;
    setEventData(eventToFormData(event));
    const { organizers, students } = extractEventParticipants(event.eventParticipants ?? []);
    setSelectedOrganizers(organizers);
    setSelectedStudents(students);
    setUnregisteredParticipants(
      mapUnregisteredFromEvent(event.eventUnregisteredParticipants ?? []),
    );
  }, [event]);

  const { establishments } = useSessionFormData();

  const establishmentName = React.useMemo(
    () =>
      establishments.find((e) => e.id === eventData.establishmentId)?.name ??
      event?.establishmentName ??
      undefined,
    [establishments, eventData.establishmentId, event],
  );

  const selectedEstablishment = React.useMemo(
    () =>
      stubEstablishmentForSelect(
        eventData.establishmentId,
        event?.establishment ?? null,
        event?.establishment?.name ?? event?.establishmentName,
      ),
    [eventData.establishmentId, event?.establishment, event?.establishmentName],
  );

  const validation = useFormValidation(eventValidationSchema);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateEventDto) => eventsService.updateEvent(eventId, data),
    onSuccess: () => {
      toast.success('Événement modifié avec succès');
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Validate (mark all fields touched on failure), lock format, attach participant ids, then PATCH. */
  function handleFieldChange(field: string, value: unknown): void {
    const updatedData = applyEventFieldSideEffects(eventData, field, value);
    setEventData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    if (field === 'startTime') validation.markAsTouched('endTime');
    if (field === 'eventDate' && eventData.eventEndDate) validation.markAsTouched('eventEndDate');
  }

  function handleBlur(field: string): void {
    validation.markAsTouched(field);
  }

  const handleSubmit = () => {
    if (!validation.validateForm(eventData)) {
      Object.keys(eventValidationSchema).forEach((field) => validation.markAsTouched(field));
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    updateMutation.mutate(
      buildUpdateEventDto(
        eventData,
        selectedOrganizers,
        selectedStudents,
        unregisteredParticipants,
      ),
    );
  };

  const selectedOrganizerIds = React.useMemo(
    () => selectedOrganizers.map((o) => o.id),
    [selectedOrganizers],
  );
  const selectedStudentIds = React.useMemo(
    () => selectedStudents.map((s) => s.id),
    [selectedStudents],
  );

  if (isLoading) {
    return (
      <div className="collapse-replace-form collapse-replace-form--loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="collapse-replace-form">
        <div className="collapse-replace-form-header">
          <div className="collapse-replace-form-header-content">
            <h2 className="collapse-replace-form-title">{getErrorMessage('errorTitle')}</h2>
          </div>
          <Button variant="ghost" size="icon" icon={X} onClick={onClose} />
        </div>
        <div className="text-center text-red-500 py-8">{getErrorMessage('loadEventFailed')}</div>
      </div>
    );
  }

  if (Object.keys(eventData).length === 0) {
    return (
      <div className="collapse-replace-form collapse-replace-form--loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="collapse-replace-form">
      <div className="collapse-replace-form-header">
        <div className="collapse-replace-form-header-content">
          <div
            className="collapse-replace-form-icon border-2"
            style={{
              background: 'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
              borderColor: 'var(--color-2-nuance-1)',
            }}
          >
            <Edit className="w-5 h-5" style={{ color: 'var(--color-2-nuance-1)' }} />
          </div>
          <div>
            <h2 className="collapse-replace-form-title">Modifier l'événement</h2>
            <p className="collapse-replace-form-subtitle">
              Modifiez les informations de l'événement
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          icon={X}
          onClick={onClose}
          disabled={updateMutation.isPending}
        />
      </div>

      <div className="collapse-replace-form-content space-y-6">
        <EventGeneralSection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          selectedEstablishment={selectedEstablishment}
        />
        <EventScheduleSection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
        />
        <EventActivitySection
          form={eventData}
          onFieldChange={handleFieldChange}
          onBlur={handleBlur}
          validation={validation}
          initialActivityTypeId={event?.activity?.activityTypeId}
          initialActivity={
            event?.activity ? { id: event.activity.id, label: event.activity.label } : null
          }
        />
        <EventFormatSection form={eventData} onFieldChange={handleFieldChange} />

        <div className="border-t border-[var(--color-1-nuance-3)] pt-6">
          <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--color-1-nuance-1)' }}>
            Participants
          </h3>
          <EventParticipantsSection
            selectedOrganizers={selectedOrganizers}
            selectedStudents={selectedStudents}
            unregisteredParticipants={unregisteredParticipants}
            selectedOrganizerIds={selectedOrganizerIds}
            selectedStudentIds={selectedStudentIds}
            onAddOrganizer={(user) => setSelectedOrganizers((prev) => [...prev, user])}
            onRemoveOrganizer={(id) =>
              setSelectedOrganizers((prev) => prev.filter((o) => o.id !== id))
            }
            onAddStudent={(user) => setSelectedStudents((prev) => [...prev, user])}
            onRemoveStudent={(id) => setSelectedStudents((prev) => prev.filter((s) => s.id !== id))}
            onAddUnregistered={(data) => setUnregisteredParticipants((prev) => [...prev, data])}
            onRemoveUnregistered={(idx) =>
              setUnregisteredParticipants((prev) => prev.filter((_, i) => i !== idx))
            }
            onEditUnregistered={(idx, data) =>
              setUnregisteredParticipants((prev) => prev.map((p, i) => (i === idx ? data : p)))
            }
            establishmentId={eventData.establishmentId}
            establishmentName={establishmentName}
            onRequestCreateTutor={onRequestCreateTutor}
          />
        </div>
      </div>

      <div className="collapse-replace-form-footer justify-end">
        <Button preset="form-cancel" onClick={onClose} disabled={updateMutation.isPending}>
          Annuler
        </Button>
        <Button preset="form-save" onClick={handleSubmit} loading={updateMutation.isPending}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
};
